import { messageDay, chatDateLabel, mergeChatMessages } from '../lib/frontDeskDates'
import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { useAuth0 } from '@auth0/auth0-react'
import { writeMemberResource } from '../lib/resourceCache'

const PAGE_SIZE = 50
const REFRESH_INTERVAL_MS = 2000
const BOTTOM_THRESHOLD_PX = 96

function formatTime(value, locale, timeZone) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
}

export default function MemberFrontDeskChat({ clubCode, mobile = false, onBack = null }) {
  const clubTimeZone = useMemberTimeZone()
  const { t, locale } = useOptionalPrivanaI18n()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const apiGetRef = useRef(memberPortalApiGet)
  apiGetRef.current = memberPortalApiGet
  const { user } = useAuth0()
  const cacheScope = useMemo(() => ({ clubCode, memberIdentity: user?.sub }), [clubCode, user?.sub])
  const authorityScopeKey = `${clubCode || ''}::${user?.sub || ''}`
  const [writeAuthority, setWriteAuthority] = useState({ scopeKey: authorityScopeKey, status: 'unresolved' })
  const currentWriteAuthority = writeAuthority.scopeKey === authorityScopeKey ? writeAuthority.status : 'unresolved'
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [draft, setDraft] = useState('')
  const [sendError, setSendError] = useState('')
  const [feedError, setFeedError] = useState('')
  const prependAnchorRef = useRef(null)
  const olderInFlightRef = useRef(false)
  const feedRef = useRef(null)
  const composerInputRef = useRef(null)
  const initialScrolledRef = useRef(false)
  const forceScrollToBottomRef = useRef(true)
  const autoScrollNextRefreshRef = useRef(true)

  const isNearBottom = useCallback(() => {
    const feed = feedRef.current
    if (!feed) return true
    const distanceToBottom = feed.scrollHeight - feed.scrollTop - feed.clientHeight
    return distanceToBottom <= BOTTOM_THRESHOLD_PX
  }, [])

  const loadInitial = useCallback(async ({ signal } = {}) => {
    if (!clubCode) return
    setLoading(true)
    setFeedError('')
    try {
      const res = await apiGetRef.current(`/api/members-portal/${clubCode}/front-desk-chat/conversation?limit=${PAGE_SIZE}`, { signal })
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) throw new Error('load_failed')
      const payload = json.data || {}
      setConversation(payload.conversation || null)
      setMessages(mergeChatMessages(Array.isArray(payload.messages) ? payload.messages : []))
      setNextCursor(payload.next_cursor || null)
      setHasMore(Boolean(payload.has_more))
      const canSend = Boolean(payload.conversation) && payload.conversation?.member_can_send !== false
      setWriteAuthority({ scopeKey: authorityScopeKey, status: canSend ? 'allowed' : 'denied' })
      writeMemberResource(cacheScope, 'frontdesk-conversation-summary', { available: Boolean(payload.conversation) })
      initialScrolledRef.current = false
      forceScrollToBottomRef.current = true
      autoScrollNextRefreshRef.current = true
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.warn('[members-frontdesk-read]', { stage: 'initial', classification: error?.name || 'network' })
      setFeedError('memberFrontDesk.loadError')
      setWriteAuthority(current => current.scopeKey === authorityScopeKey && current.status === 'allowed' ? current : { scopeKey: authorityScopeKey, status: 'unavailable' })
      // Never destroy an already rendered feed or a typed draft on refresh failure.
    } finally {
      setLoading(false)
    }
  }, [authorityScopeKey, cacheScope, clubCode])

  useEffect(() => {
    setWriteAuthority({ scopeKey: authorityScopeKey, status: 'unresolved' })
    const controller = new AbortController()
    loadInitial({ signal: controller.signal })
    return () => controller.abort()
  }, [authorityScopeKey, loadInitial])

  // A route change can unmount the chat while WebKit still considers its
  // editor active. Blur it before removal so keyboard/visual-viewport state is
  // allowed to settle instead of leaking into the next Members screen.
  useEffect(() => {
    const composer = composerInputRef.current
    return () => composer?.blur?.()
  }, [])

  useEffect(() => {
    if (!clubCode || !conversation?.id) return undefined
    let cancelled = false
    const loadLatest = async () => {
      const shouldAutoScroll = forceScrollToBottomRef.current || isNearBottom()
      autoScrollNextRefreshRef.current = shouldAutoScroll
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/front-desk-chat/conversation?limit=${PAGE_SIZE}`)
        const json = await res.json().catch(() => null)
        if (!res.ok || !json?.ok) throw new Error('refresh_failed')
        if (cancelled) return
        const payload = json.data || {}
        const latestMessages = Array.isArray(payload.messages) ? payload.messages : []
        setFeedError('')
        setConversation(payload.conversation || null)
        setMessages((current) => {
          const latestIds = new Set(latestMessages.map((message) => message.id))
          const preservedOlder = current.filter((message) => !latestIds.has(message.id))
          return mergeChatMessages(preservedOlder, latestMessages)
        })
        // The oldest loaded cursor belongs to pagination, not the latest page.
        await memberPortalApiPost(`/api/members-portal/${clubCode}/front-desk-chat/read`, {})
      } catch {
        if (!cancelled) setFeedError('memberFrontDesk.refreshError')
      }
    }
    const interval = window.setInterval(loadLatest, REFRESH_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [clubCode, conversation?.id, isNearBottom, memberPortalApiGet, memberPortalApiPost])

  useEffect(() => {
    if (!messages.length) return
    const feed = feedRef.current
    if (!feed) return
    if (initialScrolledRef.current && !forceScrollToBottomRef.current && !autoScrollNextRefreshRef.current) return
    requestAnimationFrame(() => {
      feed.scrollTop = feed.scrollHeight
      initialScrolledRef.current = true
      forceScrollToBottomRef.current = false
      autoScrollNextRefreshRef.current = true
    })
  }, [messages])

  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current
    const feed = feedRef.current
    if (!anchor || !feed) return
    feed.scrollTop = anchor.top + feed.scrollHeight - anchor.height
    prependAnchorRef.current = null
  }, [messages])

  const handleLoadOlder = useCallback(async () => {
    if (!clubCode || olderInFlightRef.current || loadingOlder || !hasMore || !nextCursor?.createdAt || !nextCursor?.id) return
    const feed = feedRef.current
    const previousHeight = feed?.scrollHeight || 0
    const previousTop = feed?.scrollTop || 0
    olderInFlightRef.current = true
    setLoadingOlder(true)
    try {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        cursor_created_at: String(nextCursor.createdAt),
        cursor_id: String(nextCursor.id),
      })
      const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/front-desk-chat/messages?${qs.toString()}`)
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) throw new Error('older_failed')
      const older = Array.isArray(json.data) ? json.data : []
      prependAnchorRef.current = { height: feed?.scrollHeight || previousHeight, top: feed?.scrollTop ?? previousTop }
      autoScrollNextRefreshRef.current = false
      setMessages((current) => mergeChatMessages(older, current))
      setNextCursor(json.next_cursor || null)
      setHasMore(Boolean(json.has_more))

    } catch {
      setFeedError('memberFrontDesk.olderError')
    } finally {
      olderInFlightRef.current = false
      setLoadingOlder(false)
    }
  }, [clubCode, hasMore, loadingOlder, memberPortalApiGet, nextCursor])

  const handleScroll = useCallback(() => {
    const feed = feedRef.current
    if (!feed) return
    autoScrollNextRefreshRef.current = isNearBottom()
    if (feed.scrollTop < 80) {
      handleLoadOlder()
    }
  }, [handleLoadOlder, isNearBottom])

  const handleSend = useCallback(async () => {
    const body = draft.trim()
    if (!body || !clubCode || currentWriteAuthority !== 'allowed') return
    setSendError('')
    const optimistic = {
      id: `optimistic-${Date.now()}`,
      sender_type: 'member',
      body,
      created_at: null,
      optimistic: true,
    }
    setMessages((current) => [...current, optimistic])
    setDraft('')
    forceScrollToBottomRef.current = true
    autoScrollNextRefreshRef.current = true
    requestAnimationFrame(() => {
      const feed = feedRef.current
      if (feed) feed.scrollTop = feed.scrollHeight
    })

    try {
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/front-desk-chat/messages`, { body })
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) throw new Error('send_failed')
      const savedConversation = json?.data?.conversation || conversation
      const savedMessage = json?.data?.message || null
      setConversation(savedConversation)
      setMessages((current) => {
        const withoutOptimistic = current.filter((entry) => entry.id !== optimistic.id)
        if (!savedMessage?.id) return withoutOptimistic
        if (withoutOptimistic.some((entry) => entry.id === savedMessage.id)) return withoutOptimistic
        return mergeChatMessages(withoutOptimistic, [savedMessage])
      })
      initialScrolledRef.current = true
    } catch {
      setSendError('memberFrontDesk.sendError')
      setDraft(body)
      setMessages((current) => current.filter((entry) => entry.id !== optimistic.id))
    }
  }, [clubCode, conversation, currentWriteAuthority, draft, memberPortalApiPost])

  const datedMessages = useMemo(() => {
    let previousDay = null
    return messages.map(message => {
      const day = message.optimistic ? null : messageDay(message.created_at, clubTimeZone)
      const separator = day && day !== previousDay
      if (day) previousDay = day
      return { message, separator }
    })
  }, [messages, clubTimeZone])

  const title = t(mobile ? 'memberFrontDesk.title' : 'memberFrontDesk.desk')

  return (
    <div className={`mp-frontdesk-chat ${mobile ? 'mp-frontdesk-chat--mobile' : ''}`} data-testid="member-contact-frontdesk-chat">
      <div className="mp-frontdesk-chat__header">
        <div className="mp-frontdesk-chat__title-wrap">
          {mobile ? (
            <button type="button" className="mp-frontdesk-chat__back" onClick={onBack} aria-label={t('memberFrontDesk.back')}>
              <ArrowLeft size={16} />
            </button>
          ) : null}
          <div>
            <div className="mp-panel-title">{title}</div>
            <div className="mp-panel-subtitle">{t('memberFrontDesk.subtitle')}</div>
          </div>
        </div>
      </div>

      <div className="mp-frontdesk-chat__feed" ref={feedRef} onScroll={handleScroll} data-testid="member-contact-frontdesk-feed">
        {loading && messages.length === 0 ? <div className="mp-resource-skeleton" aria-label={t('memberFrontDesk.loading')} /> : null}
        {loading && messages.length > 0 ? <div className="mp-panel-subtitle">{t('memberFrontDesk.refreshing')}</div> : null}
        {!loading && feedError ? <div className="mp-empty">{t(feedError)} <button type="button" className="mp-link" onClick={() => loadInitial()}>{t('memberFrontDesk.retry')}</button></div> : null}
        {!loading && !feedError && loadingOlder ? <div className="mp-empty">{t('memberFrontDesk.older')}</div> : null}
        {!loading && !feedError && messages.length === 0 ? (
          <div className="mp-empty">
            <MessageSquare size={18} />
            <span>{t('memberFrontDesk.empty')}</span>
          </div>
        ) : null}
        {datedMessages.map(({ message, separator }) => {
          const mine = message.sender_type === 'member'
          return (
            <React.Fragment key={message.id}>
            {separator ? <div className="mp-frontdesk-chat__date">{chatDateLabel(message.created_at, { locale, timeZone: clubTimeZone, t })}</div> : null}
            <div className={`mp-frontdesk-chat__row ${mine ? 'is-mine' : ''}`}>
              <div className={`mp-frontdesk-chat__bubble ${mine ? 'is-mine' : ''}`}>
                <p>{message.body}</p>
                <p className="mp-frontdesk-chat__time">{formatTime(message.created_at, locale, clubTimeZone)}</p>
              </div>
            </div>
            </React.Fragment>
          )
        })}
      </div>

      <div className="mp-frontdesk-chat__composer" data-testid="member-contact-frontdesk-composer">
        {sendError ? <p className="mp-frontdesk-chat__error">{t(sendError)}</p> : null}
        <div className="mp-frontdesk-chat__composer-row">
          <input
            ref={composerInputRef}
            className="mp-frontdesk-chat__message-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label={t('memberFrontDesk.placeholder')} placeholder={t('memberFrontDesk.placeholder')}
          />
          <button type="button" className="mp-btn" disabled={!draft.trim() || currentWriteAuthority !== 'allowed'} onClick={handleSend}>{t('memberFrontDesk.send')}</button>
        </div>
      </div>
    </div>
  )
}
